import type { SupportedFormat } from "@repo/ui/utils/supportedDateTimeFormat";

interface FormatAnyOptions {
  locale?: string;
  timeZone?: string;
  capitalizeWeekday?: boolean;
  removeDotsFromWeekday?: boolean;
}

export function toTitleCase(input: string): string {
  return input.replace(/\p{L}+/gu, (word) => {
    const [first, ...rest] = [...word];
    return first?.toLocaleUpperCase() + rest.join("").toLocaleLowerCase();
  });
}

/**
 * Formats a date string according to the specified format pattern and locale.
 *
 * Available format specifiers are based on Unicode Technical Standard #35:
 *
 * Date Components:
 * y, yy, yyyy: Year (e.g., '25', '2025')
 * M, MM, MMM, MMMM, MMMMM: Month (e.g., '1', '01', 'Jan', 'January', 'J')
 * d, dd: Day of month (e.g., '1', '01')
 * E, EE, EEE, EEEE, EEEEE: Day of week (e.g., 'Wed', 'Wednesday', 'W')
 *
 * Time Components:
 * h, hh: Hour in 12-hour format (e.g., '1', '01')
 * H, HH: Hour in 24-hour format (e.g., '13', '13')
 * m, mm: Minute (e.g., '5', '05')
 * s, ss: Second (e.g., '9', '09')
 * a: AM/PM marker (e.g., 'AM', 'PM')
 *
 * Time Zone Components:
 * z, zzzz: Time zone name (e.g., 'PST', 'Pacific Standard Time')
 * X, XX, XXX, XXXX, XXXXX: ISO 8601 time zone format (e.g., '-08', '-0800', '-08:00', '-0800', '-08:00')
 *
 * Examples (assuming date is 2025-12-31):
 * - "EEE MM dd" (en-US): "Wed 12 31"
 * - "EEE MM-dd" (es-ES): "Mié 12-31"
 * - "EEEE, MMMM dd" (en-US): "Wednesday, December 31"
 * - "dd/MM/yyyy" (es-ES): "31/12/2025"
 * - "EEE · dd MMM" (es-ES): "Mié · 31 Dic"
 *
 * @param dateString - The date to format (e.g., "2025-12-31").
 * @param format - The Unicode pattern string (e.g., "EEE MM dd").
 * @param options - An object containing the locale (e.g., { locale: "es-ES" }).
 * @returns A promise that resolves to the internationalized date string.
 */
export function getInternationalizedDate(
  date: Date | string,
  format: SupportedFormat,
  {
    locale,
    timeZone,
    capitalizeWeekday = true,
    removeDotsFromWeekday = true,
  }: FormatAnyOptions = {},
): string {
  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(parsedDate.getTime())) return "Invalid date";

  const pad = (n: number) => String(n).padStart(2, "0");

  // ---- TIME (with timezone + locale) ----
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timeZone ?? "UTC",
  });

  const timeParts = timeFormatter.formatToParts(parsedDate);
  const hourStr = timeParts.find((p) => p.type === "hour")?.value ?? pad(parsedDate.getHours());
  const minuteStr =
    timeParts.find((p) => p.type === "minute")?.value ?? pad(parsedDate.getMinutes());
  const dayStr = timeParts.find((p) => p.type === "day")?.value ?? pad(parsedDate.getDate());
  const month = timeParts.find((p) => p.type === "month")?.value ?? pad(parsedDate.getMonth() + 1);
  const yearStr = timeParts.find((p) => p.type === "year")?.value ?? pad(parsedDate.getFullYear());

  const hourNum = Number(hourStr);
  const minuteNum = Number(minuteStr);
  const dayNum = Number(dayStr);
  const monthNum = Number(month);

  // ---- DATE NUMERIC PARTS ----
  const parts = {
    yyyy: yearStr,
    yy: yearStr.slice(-2),
    M: monthNum < 10 ? month.slice(-1) : month,
    MM: pad(monthNum),
    d: dayNum < 10 ? dayStr.slice(-1) : dayStr,
    dd: pad(dayNum),

    // time tokens (24-hour clock)
    H: String(hourNum),
    HH: pad(hourNum),
    h: String(hourNum),
    hh: pad(hourNum),
    m: String(minuteNum),
    mm: pad(minuteNum),
  };

  // ---- Localized weekday and month ----
  const weekdayShort = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone: timeZone ?? "UTC",
  }).format(parsedDate);

  const weekdayLong = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: timeZone ?? "UTC",
  }).format(parsedDate);

  const monthShort = new Intl.DateTimeFormat(locale, {
    month: "short",
    timeZone: timeZone ?? "UTC",
  }).format(parsedDate);

  const monthLong = new Intl.DateTimeFormat(locale, {
    month: "long",
    timeZone: timeZone ?? "UTC",
  }).format(parsedDate);

  const cleanWeekday = (w: string) => {
    let out = removeDotsFromWeekday ? w.replaceAll(".", "") : w;
    if (capitalizeWeekday && out) {
      out = out.charAt(0).toUpperCase() + out.slice(1);
    }
    return out;
  };

  const tokens: Record<string, string> = {
    EEEE: cleanWeekday(weekdayLong),
    EEE: cleanWeekday(weekdayShort),
    MMMM: toTitleCase(monthLong),
    MMM: toTitleCase(monthShort),
    ...parts,
  };

  // Replace tokens (longest first to prevent partial matches)
  const tokenKeys = Object.keys(tokens).sort((a, b) => b.length - a.length);
  const regex = new RegExp(tokenKeys.join("|"), "g");

  const formatted = format.replace(regex, (match) => tokens[match] || match);
  return formatted.trim();
}
